import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { HardHat, History, Pencil } from "lucide-react";
import PSAAssignmentPanel from "./PSAAssignmentPanel";
import PSACategoryIcon from "./PSACategoryIcon";
import PSAPossessionEditDialog from "./psa/PSAPossessionEditDialog";

const PSA_LABEL = {
  masca: "Mască", furtun: "Furtun", curea: "Curea",
  casca: "Cască",
  cizme: "Cizme", tricou: "Tricou", hanorac: "Hanorac",
  geaca: "Geacă", bocanci: "Bocanci", pantaloni: "Pantaloni",
  altele: "Altele",
};

const ACTION_LABEL = {
  create: { icon: "➕", label: "Creat în inventar", color: "text-emerald-700" },
  move: { icon: "🔄", label: "Mutat", color: "text-blue-700" },
  assign: { icon: "✅", label: "Asignat", color: "text-cyan-700" },
  unassign: { icon: "↩", label: "Restituit", color: "text-amber-700" },
  edit: { icon: "✏️", label: "Editat", color: "text-zinc-600" },
  delete: { icon: "🗑", label: "Șters", color: "text-red-700" },
  issue: { icon: "📤", label: "Predat", color: "text-blue-700" },
  repair: { icon: "🔧", label: "Reparat", color: "text-emerald-700" },
};

const fmtTs = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const HH = String(d.getHours()).padStart(2, "0");
    const MM = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()} ${HH}:${MM}`;
  } catch { return s; }
};

const daysSince = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

/**
 * PSAPane — tab dedicat PSA pentru un angajat.
 * Afișează:
 *  - PSA-uri curent alocate (folosește PSAAssignmentPanel existent — predare/restituire)
 *  - Cost total
 *  - Istoric ultimele 200 evenimente PSA pentru persoană (asignări, restituiri, schimbări)
 */
export default function PSAPane({ personId, personName }) {
  const [history, setHistory] = useState([]);
  const [currentItems, setCurrentItems] = useState([]);
  const [persons, setPersons] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [version, setVersion] = useState(0); // forțează reload când se schimbă alocările

  useEffect(() => {
    api.get("/persons").then((r) => setPersons((r.data || []).filter((p) => p.active !== false))).catch(() => setPersons([]));
  }, []);

  useEffect(() => {
    if (!personId) return;
    setLoadingHistory(true);
    Promise.all([
      api.get(`/persons/${personId}/psa-history`).then((r) => r.data || []).catch(() => []),
      api.get(`/persons/${personId}/psa`).then((r) => r.data || []).catch(() => []),
    ])
      .then(([hist, items]) => { setHistory(hist); setCurrentItems(items); })
      .finally(() => setLoadingHistory(false));
  }, [personId, version]);

  // Statistici rapide: prima dată alocat / câte items active / câte istorice
  const stats = useMemo(() => {
    const firstAssignment = history
      .filter((h) => h.assignee_person_id === personId || h.to_assignee_person_id === personId)
      .map((h) => h.ts)
      .sort()[0]; // sortare ascendentă, primul
    const totalEvents = history.length;
    return { firstAssignment, totalEvents };
  }, [history, personId]);

  // Sumar posesie: pentru fiecare articol — cine i-a dat, când l-a primit, când și către cine l-a predat.
  // Robust la date retroactive (preluarea poate avea o dată anterioară primirii).
  const possessionRows = useMemo(() => {
    const currentIds = new Set(currentItems.map((i) => i.id));
    const nameKey = (s) => String(s || "").trim().toLowerCase().split(/\s+/).sort().join(" ");
    const pn = nameKey(personName);
    const asc = [...history].sort((a, b) => String(a.ts || "").localeCompare(String(b.ts || "")));
    const byItem = {};
    for (const h of asc) {
      if (!h.item_id) continue;
      const isReceive = h.to_assignee_person_id === personId || (h.assignee_person_id === personId && !h.to_assignee_person_id && !h.from_assignee_person_id);
      const fromPerson = h.from_assignee_person_id === personId || (!!h.from_assignee && nameKey(h.from_assignee) === pn);
      const transferredToOther = h.to_assignee_person_id && h.to_assignee_person_id !== personId;
      const cleared = !h.to_assignee && !h.to_assignee_person_id && ["move", "return", "unassign", "delete", "scrap"].includes(h.action);
      const isClose = !isReceive && (transferredToOther || cleared);
      if (!isReceive && !isClose) continue;
      const b = (byItem[h.item_id] ||= { meta: null, receives: [], closes: [] });
      if (!b.meta || (!b.meta.psa_category && h.psa_category)) b.meta = h;
      if (isReceive) {
        b.receives.push({ id: h.id, ts: h.ts, givenBy: h.picked_up_by || h.user || "—" });
      } else {
        let returnedTo;
        let toField = null;
        if (transferredToOther) { returnedTo = h.to_assignee || "altă persoană"; toField = "to_assignee"; }
        else if (h.action === "scrap") returnedTo = "Casat";
        else if (h.action === "delete") returnedTo = "Șters din inventar";
        else { returnedTo = h.to_location || "Magazie"; toField = "to_location"; }
        b.closes.push({ id: h.id, ts: h.ts, returnedTo, toField, takenBy: h.picked_up_by || h.user || "—", fromPerson });
      }
    }

    const activeRows = [];
    const closedRows = [];
    for (const [itemId, b] of Object.entries(byItem)) {
      const meta = b.meta || {};
      const base = {
        itemId,
        itemName: meta.item_name || "—",
        serial: meta.serial,
        psaCategory: meta.psa_category,
        psaSize: meta.psa_size,
      };
      const matched = new Array(b.receives.length).fill(false);
      for (const c of b.closes) {
        // Cea mai recentă primire nematchuită cu ts <= close
        let idx = -1;
        for (let i = b.receives.length - 1; i >= 0; i--) {
          if (!matched[i] && b.receives[i].ts <= c.ts) { idx = i; break; }
        }
        // Fallback retroactiv: preluarea are dată anterioară primirii (doar dacă evenimentul pleacă sigur de la persoană)
        if (idx === -1 && c.fromPerson) idx = matched.findIndex((m) => !m);
        if (idx === -1) continue;
        matched[idx] = true;
        const r = b.receives[idx];
        closedRows.push({ ...base, receivedAt: r.ts, givenBy: r.givenBy, returnedAt: c.ts, returnedTo: c.returnedTo, takenBy: c.takenBy, inPossession: false, receiveEventId: r.id, closeEventId: c.id, closeToField: c.toField });
      }
      b.receives.forEach((r, i) => {
        if (matched[i]) return;
        const stillHasIt = currentIds.has(itemId);
        activeRows.push({
          ...base, receivedAt: r.ts, givenBy: r.givenBy,
          returnedAt: null, returnedTo: stillHasIt ? null : "predat (dată neînregistrată)", takenBy: null,
          inPossession: stillHasIt,
          receiveEventId: r.id, closeEventId: null, closeToField: null,
        });
      });
    }
    // Reconciliere: articole asignate ACUM dar fără evenimente în istoric
    const seenActive = new Set(activeRows.filter((r) => r.inPossession).map((r) => r.itemId));
    for (const it of currentItems) {
      if (seenActive.has(it.id)) continue;
      activeRows.push({
        itemId: it.id,
        itemName: it.name || "—",
        serial: it.serial,
        psaCategory: it.psa_category,
        psaSize: it.psa_size,
        receivedAt: it.assigned_at,
        givenBy: it.assigned_by || "—",
        returnedAt: null, returnedTo: null, takenBy: null,
        inPossession: true,
        receiveEventId: null, closeEventId: null, closeToField: null,
      });
    }
    return [
      ...activeRows.sort((a, b) => String(b.receivedAt || "").localeCompare(String(a.receivedAt || ""))),
      ...closedRows.sort((a, b) => String(b.returnedAt || "").localeCompare(String(a.returnedAt || ""))),
    ];
  }, [history, currentItems, personId, personName]);

  if (!personId) {
    return (
      <div className="p-6 text-sm text-zinc-500 italic">
        Salvează persoana întâi pentru a vedea PSA-urile alocate.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="psa-pane">
      {/* Header informativ */}
      <div className="card-flat p-4 bg-cyan-50/40 border-l-4 border-l-cyan-500">
        <div className="flex items-center gap-2 mb-2">
          <HardHat size={20} className="text-cyan-700" />
          <h2 className="text-lg font-bold text-cyan-900">PSA alocat lui {personName}</h2>
        </div>
        <div className="text-xs text-zinc-700 grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <strong>Prima alocare:</strong>{" "}
            {stats.firstAssignment ? (
              <span data-testid="psa-pane-first-assignment">
                {fmtTs(stats.firstAssignment)}
                {daysSince(stats.firstAssignment) != null && (
                  <span className="text-zinc-500"> ({daysSince(stats.firstAssignment)} zile în urmă)</span>
                )}
              </span>
            ) : (
              <span className="italic text-zinc-500">— niciodată —</span>
            )}
          </div>
          <div>
            <strong>Evenimente PSA totale:</strong> <span data-testid="psa-pane-total-events">{stats.totalEvents}</span>
          </div>
          <div className="text-zinc-500 italic">
            ℹ Folosește butonul „Predă PSA" de mai jos pentru a aloca echipament nou.
          </div>
        </div>
      </div>

      {/* PSA curent alocat — reutilizez componenta existentă */}
      <div className="card-flat p-4">
        <PSAAssignmentPanel
          personId={personId}
          personName={personName}
          // forțează reload pe componentă când se schimbă starea
          key={`psa-panel-${version}`}
        />
        {/* Buton manual pentru refresh istoric */}
        <button
          type="button"
          onClick={() => setVersion((v) => v + 1)}
          className="mt-3 text-xs text-cyan-700 hover:text-cyan-900 underline"
          data-testid="psa-pane-refresh"
        >
          🔄 Reîncarcă istoricul
        </button>
      </div>

      {/* Sumar posesie PSA — per articol: primit / predat */}
      <div className="card-flat overflow-hidden" data-testid="psa-possession-summary">
        <div className="px-4 py-3 bg-cyan-50/60 border-b flex items-center gap-2">
          <History size={16} className="text-cyan-700" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-cyan-900">
            Sumar posesie PSA — {personName}
          </h3>
          <span className="text-xs text-zinc-500">({possessionRows.length} articole)</span>
        </div>
        {loadingHistory ? (
          <div className="p-6 text-sm text-zinc-500 italic text-center">Se încarcă...</div>
        ) : possessionRows.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500 italic text-center" data-testid="psa-possession-empty">
            Niciun PSA înregistrat pentru această persoană.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-zinc-50 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-4 py-2">Articol</th>
                  <th className="px-3 py-2">Cine i-a dat</th>
                  <th className="px-3 py-2">Data primirii</th>
                  <th className="px-3 py-2">Data predării</th>
                  <th className="px-3 py-2">Predat către</th>
                  <th className="px-3 py-2">Preluat de</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {possessionRows.map((r, idx) => (
                  <tr key={`${r.itemId}-${idx}`} className={r.inPossession ? "bg-emerald-50/40 hover:bg-emerald-50/70" : "hover:bg-zinc-50/60"} data-testid={`psa-possession-row-${idx}`}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {r.psaCategory && <PSACategoryIcon cat={r.psaCategory} size={13} className="text-cyan-700"/>}
                        <span className="font-semibold text-zinc-800">{r.itemName}</span>
                        {r.psaSize && <span className="text-[10px] px-1 py-0.5 rounded bg-cyan-100 text-cyan-900 font-bold">măs. {r.psaSize}</span>}
                        {r.serial && <span className="font-mono text-[10px] text-zinc-400">SN: {r.serial}</span>}
                        {r.inPossession && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">✓ În posesie</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-zinc-700">👤 {r.givenBy}</td>
                    <td className="px-3 py-2 text-zinc-700 whitespace-nowrap">📅 {fmtTs(r.receivedAt)}</td>
                    <td className="px-3 py-2 text-zinc-700 whitespace-nowrap">{r.returnedAt ? `📅 ${fmtTs(r.returnedAt)}` : <span className="text-zinc-400 italic">—</span>}</td>
                    <td className="px-3 py-2 text-zinc-700">{r.returnedTo ? `→ ${r.returnedTo}` : <span className="text-zinc-400 italic">—</span>}</td>
                    <td className="px-3 py-2 text-zinc-700">{r.takenBy ? `👤 ${r.takenBy}` : <span className="text-zinc-400 italic">—</span>}</td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        onClick={() => setEditRow(r)}
                        className="text-cyan-600 hover:text-cyan-900 p-1 hover:bg-cyan-50 rounded"
                        title="Editează"
                        data-testid={`psa-possession-edit-${idx}`}
                      >
                        <Pencil size={13}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Istoric complet PSA */}
      <div className="card-flat overflow-hidden">
        <div className="px-4 py-3 bg-zinc-50 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History size={16} className="text-zinc-600" />
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-700">
              Istoric PSA pentru {personName}
            </h3>
            <span className="text-xs text-zinc-500">({history.length} evenimente)</span>
          </div>
        </div>

        {loadingHistory ? (
          <div className="p-6 text-sm text-zinc-500 italic text-center">Se încarcă istoricul...</div>
        ) : history.length === 0 ? (
          <div className="p-6 text-sm text-zinc-500 italic text-center" data-testid="psa-pane-no-history">
            Niciun eveniment PSA înregistrat pentru această persoană.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100 max-h-[600px] overflow-y-auto">
            {history.map((h, idx) => {
              const action = ACTION_LABEL[h.action] || { icon: "•", label: h.action || "—", color: "text-zinc-600" };
              const isAssigned = h.assignee_person_id === personId || h.to_assignee_person_id === personId;
              const isUnassigned = h.from_assignee_person_id === personId && !h.to_assignee_person_id;
              return (
                <li
                  key={h.id || idx}
                  className="px-4 py-3 hover:bg-zinc-50/60"
                  data-testid={`psa-history-row-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-2xl flex-shrink-0">{action.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-sm font-bold ${action.color}`}>{action.label}</span>
                        {h.item_type === "psa" && h.psa_category && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-900 font-semibold">
                            <PSACategoryIcon cat={h.psa_category} size={12} className="text-cyan-800"/>
                            {PSA_LABEL[h.psa_category] || h.psa_category}
                          </span>
                        )}
                        {isAssigned && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                            → către {personName}
                          </span>
                        )}
                        {isUnassigned && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                            ← restituit
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-700">
                        <strong className="font-semibold">{h.item_name || "—"}</strong>
                        {h.serial && <span className="font-mono text-[10px] text-zinc-500 ml-1.5">SN: {h.serial}</span>}
                        {h.psa_size && <span className="text-[10px] text-cyan-700 ml-1.5">măs. {h.psa_size}</span>}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-3 flex-wrap">
                        <span>📅 {fmtTs(h.ts)}</span>
                        {h.user && <span>👤 de {h.user}</span>}
                        {h.reason && <span className="italic">💬 {h.reason}</span>}
                        {h.recipient_note && <span className="italic">📝 {h.recipient_note}</span>}
                        {(h.from_location || h.to_location) && h.from_location !== h.to_location && (
                          <span>
                            📍 <span className="font-mono">{h.from_location || "—"}</span>
                            {" → "}
                            <span className="font-mono font-semibold">{h.to_location || "—"}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {editRow && (
        <PSAPossessionEditDialog
          row={editRow}
          persons={persons}
          onClose={() => setEditRow(null)}
          onSaved={() => { setEditRow(null); setVersion((v) => v + 1); }}
        />
      )}
    </div>
  );
}
